import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'

import { RouteConfig } from '@app/routing/RenderRoutes'

import { Navigate } from '@lib/react-router-dom'

import { usePlatformRoutes } from '@pages/Tools/Platforms/PlatformsRoutes'
import { CampaignResultsPage } from '@pages/Traders-Room/Campaign-Results/CampaignResults.page'
import { CampaignContestPage } from '@pages/Traders-Room/Campaign-Results/Contest/CampaignContest.page'
import { CampaignRebatePage } from '@pages/Traders-Room/Campaign-Results/Rebate/CampaignRebate.page'
import { TradingPoolResultsPage } from '@pages/Traders-Room/Campaign-Results/Trading-Pools/TradingPoolResults.page'
import { DepositAmountPage } from '@pages/Traders-Room/Deposit/Amount/DepositAmount.page'
import { DepositCardDetailsPage } from '@pages/Traders-Room/Deposit/CardDetails/DepositCardDetails.page'
import { DepositPage } from '@pages/Traders-Room/Deposit/Deposit.page'
import { DepositFlowLayoutPage } from '@pages/Traders-Room/Deposit/DepositFlowLayout/DepositFlowLayout.page'
import { DepositRedirectPage } from '@pages/Traders-Room/Deposit/Redirect/DepositRedirect.page'
import { DepositTransactionPage } from '@pages/Traders-Room/Deposit/Transaction/DepositTransaction.page'
import { ChangeTradingAccountPasswordPage } from '@pages/Traders-Room/Trading-Accounts/ChangeTradingAccountPassword/ChangeTradingAccountPassword.page'
import { DemoTradingAccountsPage } from '@pages/Traders-Room/Trading-Accounts/Demo/DemoTradingAccounts.page'
import { DemoTradingAccountDepositPage } from '@pages/Traders-Room/Trading-Accounts/Deposit/DemoTradingAccountDeposit/DemoTradingAccountDepositPage'
import { InactiveTradingAccountsPage } from '@pages/Traders-Room/Trading-Accounts/Inactive/InactiveTradingAccounts.page'
import { LiveTradingAccountsPage } from '@pages/Traders-Room/Trading-Accounts/Live/LiveTradingAccounts.page'
import { CreateDemoTradingAccountPage } from '@pages/Traders-Room/Trading-Accounts/NewTradingAccount/Demo/CreateDemoTradingAccount.page'
import { TradingAccountsPage } from '@pages/Traders-Room/Trading-Accounts/TradingAccounts.page'
import { ConversionPage } from '@pages/Traders-Room/Transactions/Conversion/Conversion.page'
import { WithdrawalAmountPage } from '@pages/Traders-Room/Withdrawal/Amount/WithdrawalAmount.page'
import { WithdrawalTransactionPage } from '@pages/Traders-Room/Withdrawal/Transaction/WithdrawalTransaction.page'
import { WithdrawalTwoFactorPage } from '@pages/Traders-Room/Withdrawal/TwoFactor/WithdrawalTwoFactor.page'
import { WithdrawalPage } from '@pages/Traders-Room/Withdrawal/Withdrawal.page'
import { WithdrawalFlowLayoutPage } from '@pages/Traders-Room/Withdrawal/WithdrawalFlowLayout/WithdrawalFlowLayout.page'

import { useAccountReadContext } from '@context/AccountContextContext'
import { DateFilterContextProvider } from '@context/DateFilterContext'
import { useProductReadContext } from '@context/ProductContext'

import { CommonPaths } from '@utils/path'

import { WalletTypeEnum } from '@model/WalletDto'

import { BalancesPage } from './Balances/BalancesPage'
import { StockDividendsPage } from './Stock-Dividends/StockDividendsPage'
import { CreateTradingAccountCampaignPage } from './Trading-Accounts/NewTradingAccount/CreateTradingAccountCampaignPage'
import {
  CreateTradingAccountCampaignDemoSuccessPage,
  CreateTradingAccountCampaignLiveSuccessPage,
} from './Trading-Accounts/NewTradingAccount/CreateTradingAccountCampaignSuccessPage'
import { TransactionHistoryPage } from './Transaction-History/TransactionHistory.page'
import { TransactionsPage } from './Transactions/Transactions.page'
import {
  CampaignWalletTransferPage,
  TradersRoomWalletStartTransferPage,
  TradersRoomWalletTransferPage,
} from './Wallets/WalletTransfer/WalletTransfer.page'
import { WalletTransferETDPage } from './Wallets/WalletTransfer/WalletTransferETD.page'
import { TradersRoomWalletWithdrawTransactionCompletedPage } from './Wallets/WalletWithdraw/WalletWithdrawTransactionPage/TradersRoomWalletWithdrawTransactionCompletedPage'
import { TradersRoomWalletWithdrawTransactionFailedPage } from './Wallets/WalletWithdraw/WalletWithdrawTransactionPage/TradersRoomWalletWithdrawTransactionFailedPage'
import { TradersRoomWalletWithdrawTransactionSuccessPage } from './Wallets/WalletWithdraw/WalletWithdrawTransactionPage/TradersRoomWalletWithdrawTransactionSuccessPage'
import { WalletsPage } from './Wallets/Wallets.page'

const RedirectToSelection: React.FC<{ target: 'wallet' | 'trading-account' }> = ({ target }) => {
  const { walletId, tradingAccountId } = useParams<{
    walletId?: string
    tradingAccountId?: string
  }>()
  const location = useLocation()
  return (
    <Navigate
      to={`/dashboard/traders-room/deposit/${target}`}
      state={{
        ...location.state,
        selectedWalletId: walletId || location.state?.selectedWalletId,
        tradingAccountId: tradingAccountId || location.state?.tradingAccountId,
      }}
      replace
    />
  )
}

const RedirectToDeposit: React.FC<{ target: 'wallet' | 'trading-account' }> = ({ target }) => {
  const { walletId, tradingAccountId } = useParams<{
    walletId?: string
    tradingAccountId?: string
  }>()
  const location = useLocation()
  return (
    <Navigate
      to={`/dashboard/traders-room/deposit/${target}`}
      state={{
        ...location.state,
        selectedWalletId: walletId,
        tradingAccountId,
      }}
      replace
    />
  )
}

const RedirectToWithdrawal: React.FC = () => {
  const { walletId } = useParams<{ walletId?: string }>()
  const location = useLocation()
  return (
    <Navigate
      to='/dashboard/traders-room/withdrawal/wallet'
      state={{
        ...location.state,
        selectedWalletId: walletId || location.state?.selectedWalletId,
      }}
      replace
    />
  )
}

export const useTraderRoomRoutes = (): RouteConfig[] => {
  const { t } = useTranslation()
  const { account } = useAccountReadContext()
  const visible = useTraderRoomRoutesConditions()
  const { isDefaultCFDProductType } = useProductReadContext()
  const platformRoutes = usePlatformRoutes()
  const walletType = isDefaultCFDProductType() ? WalletTypeEnum.CFD : WalletTypeEnum.ETD
  const mainPath = isDefaultCFDProductType() ? 'wallets' : 'balances'

  return useMemo(
    (): RouteConfig[] => [
      {
        path: 'traders-room',
        condition: visible.isTradersRoomVisible,
        element: <Navigate to={mainPath} />,
        meta: { breadcrumbLabel: t('Tabs.Traders room') },
        children: [
          ...platformRoutes,
          {
            path: 'deposit',
            element: (
              <RedirectToSelection
                target={isDefaultCFDProductType() ? 'trading-account' : 'wallet'}
              />
            ),
            layout: 'full-no-menu',
            children: [
              {
                path: ':target',
                element: <DepositPage />,
                children: [
                  {
                    path: ':id/:pspId',
                    outlet: <DepositFlowLayoutPage />,
                    element: <Navigate to='amount' replace />,
                    children: [
                      { path: 'amount', element: <DepositAmountPage /> },
                      { path: 'card-details', element: <DepositCardDetailsPage /> },
                      { path: 'redirect', element: <DepositRedirectPage /> },
                      { path: 'transaction/:status', element: <DepositTransactionPage /> },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: 'withdrawal',
            element: <RedirectToWithdrawal />,
            layout: 'full-no-menu',
            children: [
              {
                path: ':target',
                element: <WithdrawalPage />,
                locked: account?.actionRestrictions?.tradersRoomRestricted,
                children: [
                  {
                    path: ':id/:pspId',
                    outlet: <WithdrawalFlowLayoutPage />,
                    element: <Navigate to='amount' replace />,
                    locked: account?.actionRestrictions?.tradersRoomRestricted,
                    children: [
                      { path: 'amount', element: <WithdrawalAmountPage /> },
                      { path: 'two-factor', element: <WithdrawalTwoFactorPage /> },
                      { path: 'transaction/:status', element: <WithdrawalTransactionPage /> },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: 'wallets',
            element: isDefaultCFDProductType() ? (
              <WalletsPage walletType={walletType} />
            ) : (
              <Navigate to={CommonPaths.TradersRoomBalances} />
            ),
            meta: { breadcrumbLabel: t('Tabs.Wallets'), hideChildrenBreadcrumbs: true },
            children: [
              {
                path: 'deposit',
                element: <RedirectToSelection target='wallet' />,
              },
              {
                path: 'withdrawal',
                element: <RedirectToWithdrawal />,
                locked: account?.actionRestrictions?.tradersRoomRestricted,
              },
              {
                path: 'transfer',
                condition: isDefaultCFDProductType(),
                element: <TradersRoomWalletStartTransferPage walletType={walletType} />,
                locked: account?.actionRestrictions?.openAccountRestricted,
              },
              {
                path: 'transfer',
                condition: !isDefaultCFDProductType(),
                element: <WalletTransferETDPage />,
              },
              {
                path: ':walletId',
                children: [
                  {
                    path: 'deposit',
                    element: <RedirectToDeposit target='wallet' />,
                  },
                  {
                    path: 'withdrawal',
                    element: <RedirectToWithdrawal />,
                    locked: account?.actionRestrictions?.tradersRoomRestricted,
                    children: [
                      {
                        path: 'transaction',
                        children: [
                          {
                            path: 'succeed',
                            element: <TradersRoomWalletWithdrawTransactionSuccessPage />,
                          },
                          {
                            path: 'completed',
                            element: <TradersRoomWalletWithdrawTransactionCompletedPage />,
                          },
                          {
                            path: 'failed',
                            element: <TradersRoomWalletWithdrawTransactionFailedPage />,
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'transfer',
                    element: <TradersRoomWalletStartTransferPage walletType={walletType} />,
                    condition: isDefaultCFDProductType(),
                    locked: account?.actionRestrictions?.openAccountRestricted,
                  },
                  {
                    path: 'transfer',
                    element: <WalletTransferETDPage />,
                    condition: !isDefaultCFDProductType(),
                    locked: account?.actionRestrictions?.openAccountRestricted,
                  },
                ],
              },
            ],
          },
          {
            path: 'balances',
            element: <BalancesPage walletType={walletType} />,
            condition: !isDefaultCFDProductType(),
            meta: { breadcrumbLabel: t('Tabs.Balances') },
            children: [
              {
                path: ':tradingAccountId',
                children: [
                  {
                    path: 'transfer',
                    element: <WalletTransferETDPage />,
                    meta: { breadcrumbLabel: t('Transfer') },
                  },
                ],
              },
            ],
          },
          {
            path: 'trading-accounts',
            element: <Navigate to={'./live'} />,
            outlet: <TradingAccountsPage />,
            condition: isDefaultCFDProductType(),
            meta: { breadcrumbLabel: t('Tabs.Trading Accounts') },
            children: [
              {
                path: 'live',
                element: <LiveTradingAccountsPage />,
              },
              {
                path: 'demo',
                element: <DemoTradingAccountsPage />,
              },
              {
                path: 'inactive',
                element: <InactiveTradingAccountsPage />,
              },
            ],
          },
          {
            path: 'trading-accounts',
            condition: isDefaultCFDProductType(),
            meta: { breadcrumbLabel: t('Tabs.Trading Accounts') },
            children: [
              {
                path: 'live/new',
                element: <CreateTradingAccountCampaignPage />,
                locked: account?.actionRestrictions?.openAccountRestricted,
                meta: { breadcrumbLabel: t('New') },
                children: [
                  {
                    path: 'success',
                    element: <CreateTradingAccountCampaignLiveSuccessPage />,
                    meta: { breadcrumbLabel: t('Success') },
                  },
                ],
              },
              {
                path: 'demo/new',
                element: <CreateDemoTradingAccountPage />,
                meta: { breadcrumbLabel: t('Demo') },
                children: [
                  {
                    path: 'success',
                    element: <CreateTradingAccountCampaignDemoSuccessPage />,
                    meta: { breadcrumbLabel: t('Success') },
                  },
                ],
              },
              {
                path: ':tradingAccountId',
                children: [
                  {
                    path: 'demo',
                    children: [
                      {
                        path: 'add-balance',
                        element: <DemoTradingAccountDepositPage />,
                        meta: { breadcrumbLabel: t('Add Balance') },
                      },
                    ],
                  },
                  {
                    path: 'transfer',
                    element: <TradersRoomWalletTransferPage walletType={walletType} />,
                    meta: { breadcrumbLabel: t('Transfer') },
                  },
                  {
                    path: 'change-password',
                    layout: 'full-flush',
                    element: <ChangeTradingAccountPasswordPage />,
                    meta: { breadcrumbLabel: t('Change Password') },
                  },
                  {
                    path: 'transfer-from-campaign',
                    element: <CampaignWalletTransferPage />,
                    meta: { breadcrumbLabel: t('Transfer From Campaign') },
                  },
                  {
                    path: 'deposit',
                    element: <RedirectToDeposit target='trading-account' />,
                    meta: { breadcrumbLabel: t('Deposit') },
                  },
                  {
                    path: 'deposit-to-campaign',
                    element: <RedirectToSelection target='trading-account' />,
                    meta: { breadcrumbLabel: t('Deposit') },
                  },
                  {
                    path: 'deposit-to-trading-account',
                    element: <RedirectToSelection target='trading-account' />,
                    meta: { breadcrumbLabel: t('Deposit') },
                  },
                ],
              },
            ],
          },
          {
            path: 'transactions',
            element: <TransactionsPage />,
            meta: { breadcrumbLabel: t('Tabs.Transactions') },
            children: [
              {
                path: 'conversion',
                condition: !isDefaultCFDProductType(),
                element: <ConversionPage />,
                meta: { breadcrumbLabel: t('Conversion') },
              },
            ],
          },
          {
            path: 'campaign-results',
            element: (
              <DateFilterContextProvider>
                <CampaignResultsPage />
              </DateFilterContextProvider>
            ),
            meta: { breadcrumbLabel: t('Tabs.Campaign Results') },
            children: [
              {
                path: 'contest/:accountId/:campaignId',
                element: (
                  <DateFilterContextProvider>
                    <CampaignContestPage />
                  </DateFilterContextProvider>
                ),
              },
              {
                path: 'trading-pool/:accountId/:campaignId',
                element: (
                  <DateFilterContextProvider>
                    <TradingPoolResultsPage />
                  </DateFilterContextProvider>
                ),
              },
              {
                path: 'rebate/:accountId/:campaignId',
                element: (
                  <DateFilterContextProvider>
                    <CampaignRebatePage />
                  </DateFilterContextProvider>
                ),
              },
              { path: '*', element: <Navigate to='.' /> },
            ],
          },
          {
            path: 'transaction-history',
            element: (
              <DateFilterContextProvider>
                <TransactionHistoryPage walletType={walletType} />
              </DateFilterContextProvider>
            ),
            meta: { breadcrumbLabel: t('Tabs.Transaction History') },
          },
          {
            path: 'stock-dividends',
            condition: isDefaultCFDProductType(),
            element: (
              <DateFilterContextProvider>
                <StockDividendsPage />
              </DateFilterContextProvider>
            ),
            meta: { breadcrumbLabel: t('Tabs.Stock Dividends') },
          },
        ],
      },
    ],
    [
      visible.isTradersRoomVisible,
      mainPath,
      t,
      platformRoutes,
      isDefaultCFDProductType,
      account?.actionRestrictions?.tradersRoomRestricted,
      account?.actionRestrictions?.openAccountRestricted,
      walletType,
    ]
  )
}

interface RoutesConditions {
  isTradersRoomVisible: boolean
}

export const useTraderRoomRoutesConditions = (): RoutesConditions => {
  const { account } = useAccountReadContext()
  const { isDefaultCFDProductType } = useProductReadContext()

  const isTradersRoomVisible = useMemo(
    () =>
      isDefaultCFDProductType()
        ? !!account?.visibilityConfiguration.hasTraderRoom
        : !!account?.visibilityConfiguration.hasEtdTraderRoom,
    [
      account?.visibilityConfiguration.hasEtdTraderRoom,
      account?.visibilityConfiguration.hasTraderRoom,
      isDefaultCFDProductType,
    ]
  )

  return {
    isTradersRoomVisible,
  }
}
